import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from 'ui-components/components/button';
import FloatInput from 'ui-components/components/float-input';
import ForgotPasswordService from './forgot-password-service';
import style from '../authentication.less';
import classNames from 'classnames';
import { useLocale } from '../../intl/hooks';
import LocalePicker from '../locale-picker';
import { Spanish } from '../../intl/locale-context-provider/locale-codes';
import messages from './messages';

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const intl = useIntl();

  const setErrorMessage = useCallback((msg) => {
    setErrorMsg(msg);
    setButtonDisabled(false);
  }, []);

  const setSuccessMessage = useCallback((msg) => {
    setSuccessMsg(msg);
    setButtonDisabled(false);
  }, []);

  const inProcessing = useCallback(() => {
    setButtonDisabled(true);
    setErrorMsg('');
    setSuccessMsg('');
  }, []);

  const onForgotPasswordClicked = useCallback(() => {
    inProcessing();

    if (email === '') {
      setErrorMessage(intl.formatMessage(messages.errorPleaseEnterYourEmail));
      return;
    }

    ForgotPasswordService.forgotPassword(email)
      .then(({ data }) => {
        if (!data) {
          // todo better error message here
          setErrorMessage(intl.formatMessage(messages.errorUnableToCommunicateWithServer));
          return;
        }
        if (!data.success) {
          setErrorMessage(data.message || intl.formatMessage(messages.errorWhileResettingPassword));
          return;
        }

        setSuccessMessage(messages.successPasswordReset);
      })
      .catch(() => {
        setErrorMessage(intl.formatMessage(messages.errorWhileResettingPassword));
      });
  }, [email, inProcessing, intl, setErrorMessage, setSuccessMessage]);

  const checkForEnterKey = useCallback(
    (event) => {
      if (event.key !== 'Enter') {
        return;
      }
      onForgotPasswordClicked();
    },
    [onForgotPasswordClicked],
  );

  useEffect(() => {
    document.body.addEventListener('keypress', checkForEnterKey);
    return () => {
      document.body.removeEventListener('keypress', checkForEnterKey);
    };
  }, [checkForEnterKey]);

  const renderErrorMessage = () => {
    if (!errorMsg) {
      return null;
    }

    return <div className={style['error-message']}>{errorMsg}</div>;
  };

  const renderSuccessMessage = () => {
    if (!successMsg) {
      return null;
    }
    return (
      <div className={style['success-message']}>
        <FormattedMessage {...successMsg} />
      </div>
    );
  };
  const onEmailChange = useCallback((newEmail) => setEmail(newEmail), []);

  const { locale: localeToUse } = useLocale();
  const widerAuthForm = localeToUse === Spanish ? style['wider-auth-form'] : '';
  const loginBtnMargin = {
    marginRight: localeToUse === Spanish ? '110px' : '40px',
  };

  return (
    <div className={classNames(style['auth-form'], widerAuthForm)}>
      <div className={style['auth-form-title']}>
        <FormattedMessage {...messages.resetPassword} />
      </div>
      <div className={style['auth-form-instructions']}>
        <FormattedMessage {...messages.emailInstructions} />
      </div>
      <div className={style['auth-textfield']}>
        <FloatInput label={<FormattedMessage {...messages.emailAddress} />} onChange={onEmailChange} value={email} />
      </div>

      <Button className={style['auth-button']} disabled={buttonDisabled} onClick={onForgotPasswordClicked} theme='primary'>
        <FormattedMessage {...messages.sendResetLink} />
      </Button>
      {renderErrorMessage()}
      {renderSuccessMessage()}

      <footer>
        <Button className={style['text-link']} disabled={buttonDisabled} href='/login' style={loginBtnMargin} target='' theme='link'>
          <FormattedMessage {...messages.returnToLogin} />
        </Button>
        <LocalePicker />
        <Button className={style['text-link']} disabled={buttonDisabled} href='https://www.shipstation.com/step1/' target='' theme='link'>
          <FormattedMessage {...messages.createAnAccount} />
        </Button>
      </footer>
    </div>
  );
};

export default ForgotPasswordForm;
